import React from 'react';
import { ListColumnType, ListFilterType, QueryParamType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import JobModal from '../jobs/JobModal';
import { jobFilters } from './JobList';
import { useSettings } from 'services/settings/SettingsContext';
import { renderDate, renderDateRange, renderStatusBadge } from 'components/lists/functions/renderListColumn';
import { LocationType, PersonType } from '../../views/contacts/ContactTypes';

interface JobDetailListProps {
    itemId: string;
    detailPage: string;
    data?: any;
}

const jobDetailListConfigurations: Record<string, { params: (itemId: string) => QueryParamType, dynamicColumn: ListColumnType, showAddButton?: boolean, showSearchAndFilters?: boolean, pageSize?: number }> = {
    contact: {
        params: (itemId: string) => ({ 'client': itemId }),
        dynamicColumn: { width: '4fr', field: 'work_location_address', orderField: 'work_location__address', label: 'job.general.work_location_label' }
    },
    organization: {
        params: (itemId: string) => ({ 'client': itemId }),
        dynamicColumn: { width: '4fr', field: 'work_location_address', orderField: 'work_location__address', label: 'job.general.work_location_label' }
    },
    person: {
        params: (itemId: string) => ({ 'client': itemId }),
        dynamicColumn: { width: '4fr', field: 'work_location_address', orderField: 'work_location__address', label: 'job.general.work_location_label' }
    },
    location: {
        params: (itemId: string) => ({ 'work_location': itemId }),
        dynamicColumn: { width: '4fr', field: 'client_name', orderField: 'client__name', label: 'job.general.client_label' }
    },
    asset: {
        params: (itemId: string) => ({ 'asset': itemId }),
        dynamicColumn: { width: '4fr', field: 'client_name', orderField: 'client__name', label: 'job.general.client_label' }
    },
    contract: {
        params: (itemId: string) => ({ 'contract': itemId, 'deleted': false }),
        dynamicColumn: { width: '4fr', field: 'work_location_address', orderField: 'work_location__address', label: 'job.general.work_location_label' },
        showAddButton: false,
        showSearchAndFilters: false,
        pageSize: 6
    }
};

const JobDetailList: React.FC<JobDetailListProps> = ({ itemId, detailPage, data }) => {
    const { userLocale } = useSettings();

    // Get the job detail list configuration
    const configuration = jobDetailListConfigurations[detailPage];

    // Get the default params and dynamic column from the configuration
    const defaultParams = configuration.params(itemId);
    const dynamicColumn = configuration.dynamicColumn;

    // Specify the columns of the contract page, the location is already known there
    const contractColumns: ListColumnType[] = [
        {
            width: '2.5fr',
            field: 'number',
            label: 'job.general.job_number_label'
        },
        {
            width: '4fr',
            field: 'internal_reference',
            label: 'contract.general.reference_label'
        },
        {
            width: '2.5fr',
            field: 'target_date',
            label: 'job.general.target_date_label',
            render: (_, row) => renderDate(row.target_date, userLocale)
        },
        {
            width: '2.5fr',
            field: 'appointment_range',
            label: 'job.general.appointment_date',
            orderField: 'first_appointment_start',
            render: (_, row) => renderDateRange(row.first_appointment_start, row.last_appointment_end, userLocale)
        },
        {
            width: '144px',
            field: 'status',
            label: 'job.general.status_label',
            render: (_, row) => renderStatusBadge(row.status_name, row.status_condition)
        }
    ];

    // Specify the default columns of this list
    const defaultColumns: ListColumnType[] = [
        {
            width: '2.5fr',
            field: 'number',
            label: 'job.general.job_number_label'
        },
        dynamicColumn,
        {
            width: '6fr',
            field: 'main_description_plain',
            orderField: 'main_description',
            label: 'job.general.main_description_label'
        },
        {
            width: '144px',
            field: 'status',
            label: 'job.general.status_label',
            render: (_, row) => renderStatusBadge(row.status_name, row.status_condition)
        }
    ];

    // Use the contract columns on the contract page
    const columns = detailPage === 'contract' ? contractColumns : defaultColumns;

    // Add the page params to the default list filters
    const applyPageParamsToFilters = (filters: ListFilterType[], pageParams: QueryParamType) => {
        return filters.map(filter => {
            if ('filters' in filter) {
                filter.filters = filter.filters.map(subFilter => ({
                    ...subFilter,
                    params: { ...subFilter.params, ...pageParams }
                }));
            } else {
                filter.params = { ...(filter.params || {}), ...pageParams };
            }
            return filter;
        });
    };

    // Configure post new item object
    const postNewItem = (detailPage: string) => {
        let postDataConfig: { url: string; linkedItem?: Record<string, string | number>} = { url: 'post_job' };
        let linkedItem: Record<string, string | number> | undefined;

        switch (detailPage) {
            case 'contact':
                // Get the primary location of the contact
                const contactPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the work locations of the contact
                const contactWorkLocations = data.work_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the primary persons of the contact
                const contactPrimaryPersons = data.primary_persons?.filter((person: PersonType) => !person.deleted) || [];

                linkedItem = { 
                    'client': itemId,
                    'reporting_person': contactPrimaryPersons.length === 1 ? contactPrimaryPersons[0].contact : null,
                    'work_location': contactPrimaryLocations.length === 1 && contactWorkLocations.length === 0 ? contactPrimaryLocations[0].id : null,
                };
                break;
            case 'organization':
                // Get the primary locations of the organization
                const organizationPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the work locations of the organization
                const organizationWorkLocations = data.work_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the primary persons of the organization
                const organizationPrimaryPersons = data.primary_persons?.filter((person: PersonType) => !person.deleted) || [];

                linkedItem = { 
                    'client': itemId,
                    'reporting_person': organizationPrimaryPersons.length === 1 ? organizationPrimaryPersons[0].contact : null,
                    'invoice_address': organizationPrimaryLocations.length === 1 ? organizationPrimaryLocations[0].id : null,
                    'work_location': organizationPrimaryLocations.length === 1 && organizationWorkLocations.length === 0 ? organizationPrimaryLocations[0].id : null,
                    'location_person': organizationPrimaryPersons.length === 1 && organizationWorkLocations.length === 0 ? organizationPrimaryPersons[0].contact : null,
                };
                break;
            case 'person':
                // Get the primary location of the person
                const personPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                linkedItem = { 
                    'client': itemId,
                    'work_location': personPrimaryLocations.length === 1 ? personPrimaryLocations[0].id : null,
                };
                break;
            case 'location':
                linkedItem = {
                    'work_location': itemId
                };
                break;
            case 'contract':
                linkedItem = {
                    'contract': itemId,
                    'client': data?.client?.id ?? null,
                    'work_location': data?.work_location?.id ?? null,
                    'location_person': data?.location_person?.id ?? null,
                };
                break;
            default:
                break;
        }

        // Add the linked item to the post data config
        if (linkedItem) {
            postDataConfig.linkedItem = {
                plan_method: 'add_to_storage',
                ...linkedItem,
            };
        }

        return postDataConfig;
    };

    return (
        <div className='detail-list'>
            <AbstractList
                query={{
                    endpoint: 'get_job_list',
                    defaultParams,
                    object: 'job'
                }}
                objectName='job.general.object_name'
                columns={columns}
                filters={configuration.showSearchAndFilters !== false ? applyPageParamsToFilters(jobFilters, defaultParams) : undefined}
                showSearch={configuration.showSearchAndFilters !== false}
                buttonSize='small'
                isPaginated={true}
                pageSize={configuration.pageSize}
                showAddButton={configuration.showAddButton !== false}
                postNewItem={postNewItem(detailPage)}
                detailModal={<JobModal />}
                onRowClick='detailModal'
            />
        </div>
    );
};

export default JobDetailList;